import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  Loader2, Download, FileSpreadsheet, Calendar, Building2, ChevronLeft, ChevronRight,
  IndianRupee, Users, XCircle, CreditCard, BarChart3, Briefcase, RefreshCw, FileText,
  Coins, BookOpen, Layers, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';

const REPORTS = [
  { id: 'all-bookings', label: 'All Bookings (Master)', icon: Layers, path: 'all-bookings', filename: 'all_bookings_master' },
  { id: 'day-pass', label: 'Day Pass', icon: Briefcase, path: 'day-pass', filename: 'day_pass_bookings' },
  { id: 'meeting-rooms', label: 'Meeting Rooms', icon: Calendar, path: 'meeting-rooms', filename: 'meeting_room_bookings' },
  { id: 'revenue', label: 'Revenue', icon: IndianRupee, path: 'revenue', filename: 'revenue' },
  { id: 'occupancy', label: 'Occupancy', icon: BarChart3, path: 'occupancy', filename: 'occupancy' },
  { id: 'customers', label: 'Customers', icon: Users, path: 'customers', filename: 'customer_master' },
  { id: 'cancellations', label: 'Cancellations & Refunds', icon: XCircle, path: 'cancellations', filename: 'cancellations_refunds' },
  { id: 'payment-reconciliation', label: 'Payment Reconciliation', icon: CreditCard, path: 'payment-reconciliation', filename: 'payment_reconciliation' },
  { id: 'credits-utilization', label: 'Credits Utilisation', icon: Coins, path: 'credits-utilization', filename: 'credits_utilisation' },
  { id: 'credits-ledger', label: 'Total Credits Ledger', icon: BookOpen, path: 'credits-ledger', filename: 'credits_ledger' },
];

const CREATOR_ROLE_OPTIONS = [
  { v: 'all', l: 'All Creators' },
  { v: 'client', l: 'Client' },
  { v: 'center_manager', l: 'Center Manager' },
  { v: 'cluster_manager', l: 'Cluster Manager' },
  { v: 'sales', l: 'Sales' },
  { v: 'admin', l: 'Admin' },
  { v: 'hub_manager', l: 'Hub Manager' },
  { v: 'guest', l: 'Guest (no login)' },
  { v: 'internal', l: 'Internal' },
];

const INVOICE_STATUS_OPTIONS = [
  { v: 'all', l: 'All Invoice Statuses' },
  { v: 'generated', l: 'Generated' },
  { v: 'pending', l: 'Pending' },
  { v: 'missing', l: 'Missing' },
  { v: 'not_applicable', l: 'N/A' },
];

const BOOKABLE_TYPE_OPTIONS = [
  { v: 'all', l: 'All Inventory Types' },
  { v: 'meeting_room', l: 'Meeting Room' },
  { v: 'conference_room', l: 'Conference Room' },
  { v: 'day_pass', l: 'Day Pass' },
  { v: 'dedicated_desk', l: 'Dedicated Desk' },
  { v: 'event_space', l: 'Event Space' },
  { v: 'virtual_office', l: 'Virtual Office' },
];

// Quick date presets (returns {from_date, to_date})
const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_7', label: 'Last 7 days' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_30', label: 'Last 30 days' },
  { id: 'this_quarter', label: 'This Quarter' },
  { id: 'this_year', label: 'This Year' },
  { id: 'all', label: 'All Time' },
];

const computePreset = (id) => {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const start = new Date(today);
  const end = new Date(today);
  switch (id) {
    case 'today': return { from_date: iso(today), to_date: iso(today) };
    case 'yesterday': start.setDate(start.getDate() - 1); return { from_date: iso(start), to_date: iso(start) };
    case 'this_week': start.setDate(today.getDate() - today.getDay()); return { from_date: iso(start), to_date: iso(end) };
    case 'last_7': start.setDate(start.getDate() - 6); return { from_date: iso(start), to_date: iso(end) };
    case 'this_month': return { from_date: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to_date: iso(end) };
    case 'last_30': start.setDate(start.getDate() - 29); return { from_date: iso(start), to_date: iso(end) };
    case 'this_quarter': {
      const q = Math.floor(today.getMonth() / 3);
      return { from_date: iso(new Date(today.getFullYear(), q * 3, 1)), to_date: iso(end) };
    }
    case 'this_year': return { from_date: iso(new Date(today.getFullYear(), 0, 1)), to_date: iso(end) };
    default: return { from_date: '', to_date: '' };
  }
};

const PAYMENT_METHOD_OPTIONS = [
  { v: 'all', l: 'All Methods' },
  { v: 'internal', l: 'Internal' },
  { v: 'credits', l: 'Credits' },
  { v: 'razorpay_card', l: 'Razorpay — Card' },
  { v: 'razorpay_netbanking', l: 'Razorpay — Net Banking' },
  { v: 'razorpay_upi', l: 'Razorpay — UPI' },
  { v: 'razorpay_wallet', l: 'Razorpay — Wallet' },
  { v: 'razorpay_other', l: 'Razorpay — Other' },
  { v: 'unpaid', l: 'Unpaid' },
  { v: 'other', l: 'Other' },
];

const TXN_TYPE_OPTIONS = [
  { v: 'all', l: 'All Types' },
  { v: 'purchase', l: 'Purchase' },
  { v: 'allocation', l: 'Allocation' },
  { v: 'auto_renewal', l: 'Auto Renewal' },
  { v: 'booking', l: 'Booking Debit' },
  { v: 'refund', l: 'Refund' },
  { v: 'adjustment', l: 'Adjustment' },
  { v: 'vo_initial_allocation', l: 'VO Initial Allocation' },
];

const STATUS_OPTIONS = [
  { v: 'all', l: 'All Statuses' },
  { v: 'confirmed', l: 'Confirmed' },
  { v: 'completed', l: 'Completed' },
  { v: 'pending', l: 'Pending' },
  { v: 'pending_payment', l: 'Pending Payment' },
  { v: 'cancelled', l: 'Cancelled' },
];

const CURRENCY_COLS = new Set([
  'amount', 'gross', 'gst', 'net', 'total_spend', 'refund_amount', 'total_amount',
  'base_amount', 'gst_amount', 'add_ons_amount', 'discount_amount', 'coupon_discount',
  'net_excl_gst', 'inr_amount',
]);

const formatCell = (col, val) => {
  if (val === null || val === undefined || val === '') return '—';
  if (CURRENCY_COLS.has(col)) {
    const n = Number(val); if (!isNaN(n)) return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }
  if (col === 'credits_used' || col === 'credits_required' || col === 'credits_amount') {
    const n = Number(val); if (!isNaN(n)) return `${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })} cr`;
  }
  if (col === 'utilisation_pct') return `${val}%`;
  if (col === 'is_internal') return val === true || val === 'True' || val === 'true' ? 'Yes' : 'No';
  if (col === 'created_at' || col === 'cancelled_at' || col === 'paid_at') {
    const d = new Date(val); if (!isNaN(d.getTime())) return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  if (col === 'date') return String(val);
  if (col === 'notes') {
    const s = String(val); return s.length > 80 ? s.slice(0, 80) + '…' : s;
  }
  return String(val);
};

const titleize = (s) => String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ReportsTab = ({ api, cardBg, cardBorder, innerCardBg, inputBg }) => {
  const [activeReport, setActiveReport] = useState('all-bookings');
  const [venues, setVenues] = useState([]);
  const [filters, setFilters] = useState({
    from_date: '', to_date: '', venue_id: 'all', status: 'all',
    page: 1, page_size: 50, group_by: 'centre',
    payment_method: 'all', transaction_type: 'all',
    creator_role: 'all', invoice_status: 'all', bookable_type: 'all',
    preset: 'all',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fetch venues for the centre filter
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/venues');
        setVenues(res.data || []);
      } catch (e) {
        // venues list optional — fall back to free-text id
      }
    })();
  }, [api]);

  const buildParams = useCallback((overrides = {}, includePagination = true) => {
    const p = new URLSearchParams();
    const f = { ...filters, ...overrides };
    if (f.from_date) p.append('from_date', f.from_date);
    if (f.to_date) p.append('to_date', f.to_date);
    if (f.venue_id && f.venue_id !== 'all') p.append('venue_id', f.venue_id);
    if (f.status && f.status !== 'all' && (activeReport === 'day-pass' || activeReport === 'meeting-rooms' || activeReport === 'payment-reconciliation')) {
      p.append('status', f.status);
    }
    if (activeReport === 'revenue' && f.group_by) p.append('group_by', f.group_by);
    if (activeReport === 'payment-reconciliation' && f.payment_method && f.payment_method !== 'all') {
      p.append('payment_method', f.payment_method);
    }
    if (activeReport === 'credits-ledger' && f.transaction_type && f.transaction_type !== 'all') {
      p.append('transaction_type', f.transaction_type);
    }
    if (activeReport === 'all-bookings') {
      if (f.payment_method && f.payment_method !== 'all') p.append('payment_method', f.payment_method);
      if (f.creator_role && f.creator_role !== 'all') p.append('creator_role', f.creator_role);
      if (f.invoice_status && f.invoice_status !== 'all') p.append('invoice_status', f.invoice_status);
      if (f.bookable_type && f.bookable_type !== 'all') p.append('bookable_type', f.bookable_type);
      if (f.status && f.status !== 'all') p.append('status', f.status);
    }
    if (includePagination) {
      p.append('page', f.page);
      p.append('page_size', f.page_size);
    }
    return p.toString();
  }, [filters, activeReport]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const path = REPORTS.find((r) => r.id === activeReport).path;
      const res = await api.get(`/reports/${path}?${buildParams()}`);
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Failed to load report';
      toast.error(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeReport, buildParams, api]);

  // eslint-disable-next-line
  useEffect(() => { fetchData(); }, [activeReport, filters.page, filters.page_size, filters.group_by, filters.from_date, filters.to_date, filters.venue_id, filters.status, filters.payment_method, filters.transaction_type, filters.creator_role, filters.invoice_status, filters.bookable_type, fetchData]);

  const onApply = () => {
    setFilters((f) => ({ ...f, page: 1 }));
  };

  const onReset = () => {
    setFilters({ from_date: '', to_date: '', venue_id: 'all', status: 'all', page: 1, page_size: 50, group_by: 'centre', payment_method: 'all', transaction_type: 'all', creator_role: 'all', invoice_status: 'all', bookable_type: 'all', preset: 'all' });
  };

  const applyPreset = (id) => {
    const { from_date, to_date } = computePreset(id);
    setFilters((f) => ({ ...f, from_date, to_date, preset: id, page: 1 }));
  };

  const downloadFile = async (format) => {
    setExporting(true);
    try {
      const path = REPORTS.find((r) => r.id === activeReport).path;
      const fname = REPORTS.find((r) => r.id === activeReport).filename;
      // request without pagination → all matching rows
      const qs = buildParams({}, false) + `&format=${format}`;
      const res = await api.get(`/reports/${path}?${qs}`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fname}_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setExporting(false);
    }
  };

  // Hide these from the visible table — surfaced as buttons / badges instead
  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const totalPages = data?.total_pages || 0;
  const totalRows = data?.total || 0;

  const HIDDEN_COLS = new Set(['invoice_id', 'invoice_url']);
  const visibleColumns = columns.filter((c) => !HIDDEN_COLS.has(c));
  const hasInvoiceCol = columns.includes('invoice_number') || columns.includes('invoice_url');

  const downloadInvoice = async (bookingId, invoiceUrl) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank', 'noopener');
      return;
    }
    if (!bookingId) {
      toast.error('No invoice available for this booking');
      return;
    }
    try {
      const res = await api.get(`/bookings/${bookingId}/invoice/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${bookingId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invoice not yet generated');
    }
  };

  const renderCell = (col, row) => {
    const val = row[col];
    if (col === 'invoice_number') {
      const inv = row.invoice_number;
      const url = row.invoice_url;
      if (!inv && row.invoice_status === 'not_applicable') return <Badge variant="outline" className="text-[10px]">N/A</Badge>;
      if (!inv) return <span className="text-muted-foreground">—</span>;
      return (
        <button
          onClick={() => downloadInvoice(row.booking_id, url)}
          className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
          data-testid={`invoice-dl-${row.booking_id}`}
        >
          <FileDown className="w-3.5 h-3.5" />
          {inv}
        </button>
      );
    }
    if (col === 'invoice_status') {
      const map = {
        generated: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        pending: 'bg-amber-50 text-amber-700 border-amber-200',
        missing: 'bg-rose-50 text-rose-700 border-rose-200',
        not_applicable: 'bg-slate-100 text-slate-600 border-slate-200',
      };
      return <Badge className={`${map[val] || 'bg-slate-50 text-slate-700'} border text-[10px]`}>{titleize(val || '—')}</Badge>;
    }
    if (col === 'booking_status' || col === 'payment_status') {
      const map = {
        confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        completed: 'bg-blue-50 text-blue-700 border-blue-200',
        pending: 'bg-amber-50 text-amber-700 border-amber-200',
        pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
        cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
        no_show: 'bg-slate-100 text-slate-600 border-slate-200',
      };
      return <Badge className={`${map[val] || 'bg-slate-50 text-slate-700'} border text-[10px]`}>{titleize(val || '—')}</Badge>;
    }
    if (col === 'payment_method' && val) {
      return <Badge variant="outline" className="text-[10px]">{titleize(val)}</Badge>;
    }
    if (col === 'created_by_role' && val) {
      return <Badge variant="outline" className="text-[10px]">{titleize(val)}</Badge>;
    }
    return formatCell(col, val);
  };

  return (
    <div className="space-y-6 min-w-0 max-w-full" data-testid="reports-tab">
      {/* Read-only banner */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Finance Reports
          </h2>
          <p className="text-sm text-muted-foreground mt-1">View-only reports with CSV / Excel export.</p>
        </div>
        <Badge variant="outline" className="text-xs py-1.5">Read-only · Audit access</Badge>
      </div>

      {/* Report selector pills */}
      <div className="flex flex-wrap gap-2" data-testid="reports-selector">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          const active = activeReport === r.id;
          return (
            <button
              key={r.id}
              data-testid={`report-tab-${r.id}`}
              onClick={() => { setActiveReport(r.id); setFilters((f) => ({ ...f, page: 1 })); }}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all flex items-center gap-2 ${
                active ? 'bg-primary text-primary-foreground border-primary' : `${cardBg || 'bg-card'} ${cardBorder || 'border-border'} hover:ring-1 hover:ring-primary/30`
              }`}
            >
              <Icon className="w-4 h-4" />
              {r.label}
            </button>
          );
        })}
      </div>

      {/* Quick date presets */}
      <div className="flex flex-wrap gap-2" data-testid="reports-date-presets">
        {DATE_PRESETS.map((p) => (
          <button
            key={p.id}
            data-testid={`preset-${p.id}`}
            onClick={() => applyPreset(p.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              filters.preset === p.id
                ? 'bg-primary text-primary-foreground border-primary'
                : `${cardBg || 'bg-card'} ${cardBorder || 'border-border'} hover:ring-1 hover:ring-primary/30`
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <Card className={cardBg}>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">From Date</label>
            <Input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value, preset: 'custom' }))}
              data-testid="reports-from-date"
              className={inputBg}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">To Date</label>
            <Input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value, preset: 'custom' }))}
              data-testid="reports-to-date"
              className={inputBg}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Centre</label>
            <Select value={filters.venue_id} onValueChange={(v) => setFilters((f) => ({ ...f, venue_id: v }))}>
              <SelectTrigger data-testid="reports-venue-filter" className={inputBg}><SelectValue placeholder="All Centres" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Centres</SelectItem>
                {venues.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(activeReport === 'day-pass' || activeReport === 'meeting-rooms' || activeReport === 'payment-reconciliation' || activeReport === 'all-bookings') && (
            <div>
              <label className="text-xs text-muted-foreground">Status</label>
              <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="reports-status-filter" className={inputBg}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {activeReport === 'all-bookings' && (
            <>
              <div>
                <label className="text-xs text-muted-foreground">Inventory Type</label>
                <Select value={filters.bookable_type} onValueChange={(v) => setFilters((f) => ({ ...f, bookable_type: v, page: 1 }))}>
                  <SelectTrigger data-testid="reports-inv-type" className={inputBg}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BOOKABLE_TYPE_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Booked By (Role)</label>
                <Select value={filters.creator_role} onValueChange={(v) => setFilters((f) => ({ ...f, creator_role: v, page: 1 }))}>
                  <SelectTrigger data-testid="reports-creator-role" className={inputBg}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CREATOR_ROLE_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Invoice Status</label>
                <Select value={filters.invoice_status} onValueChange={(v) => setFilters((f) => ({ ...f, invoice_status: v, page: 1 }))}>
                  <SelectTrigger data-testid="reports-invoice-status" className={inputBg}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INVOICE_STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Payment Method</label>
                <Select value={filters.payment_method} onValueChange={(v) => setFilters((f) => ({ ...f, payment_method: v, page: 1 }))}>
                  <SelectTrigger data-testid="reports-master-pm" className={inputBg}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHOD_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {activeReport === 'revenue' && (
            <div>
              <label className="text-xs text-muted-foreground">Group By</label>
              <Select value={filters.group_by} onValueChange={(v) => setFilters((f) => ({ ...f, group_by: v, page: 1 }))}>
                <SelectTrigger data-testid="reports-groupby" className={inputBg}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="centre">Centre</SelectItem>
                  <SelectItem value="product">Product Type</SelectItem>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="payment_method">Payment Method</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {activeReport === 'payment-reconciliation' && (
            <div>
              <label className="text-xs text-muted-foreground">Payment Method</label>
              <Select value={filters.payment_method} onValueChange={(v) => setFilters((f) => ({ ...f, payment_method: v, page: 1 }))}>
                <SelectTrigger data-testid="reports-pm-filter" className={inputBg}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHOD_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {activeReport === 'credits-ledger' && (
            <div>
              <label className="text-xs text-muted-foreground">Transaction Type</label>
              <Select value={filters.transaction_type} onValueChange={(v) => setFilters((f) => ({ ...f, transaction_type: v, page: 1 }))}>
                <SelectTrigger data-testid="reports-txn-filter" className={inputBg}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TXN_TYPE_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-end gap-2 col-span-1 md:col-span-2 lg:col-span-1">
            <Button onClick={onApply} className="flex-1" data-testid="reports-apply-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Apply
            </Button>
            <Button onClick={onReset} variant="outline" data-testid="reports-reset-btn">Reset</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      {summary && Object.keys(summary).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="reports-summary">
          {Object.entries(summary).filter(([k, v]) => k !== 'note' && typeof v !== 'object').map(([k, v]) => (
            <Card key={k} className={cardBg}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">{titleize(k)}</p>
                <p className="text-2xl font-bold mt-1">
                  {typeof v === 'number' && (k.includes('amount') || k.includes('spend') || k.includes('gross') || k.includes('gst') || k.includes('net') || k.includes('paid') || k.includes('refund') || k.includes('inr'))
                    ? `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                    : (typeof v === 'number' && k.includes('credits'))
                      ? `${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })} cr`
                      : (typeof v === 'number' && k.includes('pct')) ? `${v}%` : String(v)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Breakdown cards for nested aggregates (by_method / by_type / by_org / by_status / by_role / by_invoice) */}
      {summary && (
        <>
          {summary.by_method && (
            <Card className={cardBg}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Breakdown by Payment Method</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 pt-0">
                {Object.entries(summary.by_method).map(([m, t]) => (
                  <div key={m} className={`p-3 rounded-md border ${cardBorder || ''}`}>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{titleize(m)}</p>
                    <p className="text-sm font-bold">
                      {typeof t === 'object' ? `${t.count} · ₹${Number(t.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : t}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {summary.by_status && (
            <Card className={cardBg}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Breakdown by Booking Status</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 pt-0">
                {Object.entries(summary.by_status).map(([s, c]) => (
                  <div key={s} className={`p-3 rounded-md border ${cardBorder || ''}`}>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{titleize(s)}</p>
                    <p className="text-sm font-bold">{c}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {summary.by_role && (
            <Card className={cardBg}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Bookings by Creator Role</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 pt-0">
                {Object.entries(summary.by_role).map(([r, c]) => (
                  <div key={r} className={`p-3 rounded-md border ${cardBorder || ''}`}>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{titleize(r)}</p>
                    <p className="text-sm font-bold">{c}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {summary.by_invoice && (
            <Card className={cardBg}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Breakdown by Invoice Status</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-0">
                {Object.entries(summary.by_invoice).map(([s, c]) => (
                  <div key={s} className={`p-3 rounded-md border ${cardBorder || ''}`}>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{titleize(s)}</p>
                    <p className="text-sm font-bold">{c}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {summary.by_type && (
            <Card className={cardBg}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Breakdown by Transaction Type</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 pt-0">
                {Object.entries(summary.by_type).map(([t, v]) => (
                  <div key={t} className={`p-3 rounded-md border ${cardBorder || ''}`}>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{titleize(t)}</p>
                    <p className="text-sm font-bold">{v.count} · {Number(v.credits || 0).toFixed(2)} cr</p>
                    {v.inr > 0 && <p className="text-xs text-muted-foreground">₹{Number(v.inr).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
      {summary?.note && <p className="text-xs text-muted-foreground italic">{summary.note}</p>}

      {/* Action bar — export */}
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <div className="text-sm text-muted-foreground flex items-center gap-3">
          <span>{loading ? 'Loading…' : `Showing ${rows.length} of ${totalRows} rows`}</span>
          <div className="flex items-center gap-1">
            <span className="text-xs">Per page:</span>
            <Select value={String(filters.page_size)} onValueChange={(v) => setFilters((f) => ({ ...f, page_size: Number(v), page: 1 }))}>
              <SelectTrigger className="w-20 h-8" data-testid="reports-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[25, 50, 100, 200, 500].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => downloadFile('csv')}
            disabled={exporting || !rows.length}
            data-testid="reports-export-csv"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadFile('xlsx')}
            disabled={exporting || !rows.length}
            data-testid="reports-export-xlsx"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Export Excel
          </Button>
        </div>
      </div>

      {/* Data table */}
      <Card className={`${cardBg} overflow-hidden`}>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-40" />
              <p>No data for selected filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-w-full">
              <table className="text-sm" data-testid="reports-table">
                <thead className={`${innerCardBg || 'bg-muted/30'} border-b`}>
                  <tr>
                    {visibleColumns.map((c) => (
                      <th key={c} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">
                        {titleize(c)}
                      </th>
                    ))}
                    {hasInvoiceCol && (
                      <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} className="border-b last:border-0 hover:bg-muted/20">
                      {visibleColumns.map((c) => (
                        <td key={c} className="px-4 py-2.5 whitespace-nowrap">{renderCell(c, row)}</td>
                      ))}
                      {hasInvoiceCol && (
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          {row.invoice_number || row.invoice_url ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadInvoice(row.booking_id, row.invoice_url)}
                              data-testid={`row-invoice-${row.booking_id}`}
                              className="h-7 text-xs"
                            >
                              <FileDown className="w-3 h-3 mr-1" /> Invoice
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center" data-testid="reports-pagination">
          <div className="text-sm text-muted-foreground">Page {filters.page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              disabled={filters.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
              data-testid="reports-page-prev"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </Button>
            <Button
              variant="outline" size="sm"
              disabled={filters.page >= totalPages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
              data-testid="reports-page-next"
            >
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
